import * as React from 'react';

import {
  Button,
  ButtonProps,
  Col,
  Popover,
  PopoverDialog,
  PopoverTrigger,
  Row,
  cn,
} from '@rematter/ui-web';
import { LucideTrash2 } from 'lucide-react';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';

interface ConfirmPopoverButtonProps {
  children?: React.ReactNode;
  onConfirm: () => void | Promise<void>;
  title?: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  buttonProps?: ButtonProps;
  isLoading?: boolean;
  renderTrigger?: (
    props: ButtonProps & { isLoading: boolean },
  ) => React.ReactNode;
  className?: string;
  contentClassName?: string;
}

//
// ⚛️ ⚛️ ⚛️ CONFIRM POPOVER BUTTON ⚛️ ⚛️ ⚛️
//
export const ConfirmPopoverButton: React.FC<ConfirmPopoverButtonProps> = ({
  children,
  onConfirm,
  title,
  message,
  confirmLabel,
  cancelLabel,
  buttonProps,
  isLoading = false,
  renderTrigger,
  className,
  contentClassName,
}) => {
  const t = useTranslations();

  // States
  const [isOpen, setIsOpen] = React.useState(false);
  const [isPending, startTransition] = React.useTransition();

  const i18nMessage = message ?? t.confirmPopoverButton.message;
  const i18nConfirm = confirmLabel ?? t.confirmPopoverButton.confirm;
  const i18nDiscard = cancelLabel ?? t.confirmPopoverButton.discard;

  // Callbacks
  const handleConfirm = React.useCallback(() => {
    startTransition(async () => {
      await onConfirm();
    });
  }, [onConfirm]);

  const handleDiscard = React.useCallback(() => setIsOpen(false), []);

  const triggerElement = renderTrigger ? (
    renderTrigger({ ...buttonProps, isLoading: isPending || isLoading })
  ) : (
    <Button {...buttonProps} icon={<LucideTrash2 />}>
      {children}
    </Button>
  );

  return (
    <span className={cn('@ConfirmPopoverButton', className)}>
      <PopoverTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
        {triggerElement}
        <Popover>
          <PopoverDialog>
            <Col gap4 className={cn('min-w-[220px] p-4', contentClassName)}>
              {title && (
                <div className="text-sm font-semibold text-neutral">
                  {title}
                </div>
              )}
              <div className="text-sm text-neutral/80">{i18nMessage}</div>
              <Row xEnd gap2>
                <Button
                  variant="outline"
                  color="neutral"
                  onPress={handleDiscard}
                  isLoading={isPending || isLoading}
                >
                  {i18nDiscard}
                </Button>

                <Button
                  variant="solid"
                  color="failure"
                  onPress={handleConfirm}
                  isLoading={isPending || isLoading}
                >
                  {i18nConfirm}
                </Button>
              </Row>
            </Col>
          </PopoverDialog>
        </Popover>
      </PopoverTrigger>
    </span>
  );
};
